import React from 'react';
import { Phone, Voicemail, Mail, MessageCircle, Linkedin, CalendarCheck, Reply, Search, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';

export function ActivityLedger({ report }) {
  const a = report.activity;
  const tiles = [
    { icon: Phone, label: 'Calls placed', value: a.calls_total, sub: `${a.connected} connected · ${a.positive_outcomes} positive`, testId: 'act-calls' },
    { icon: Voicemail, label: 'Voicemails left', value: a.voicemails, sub: `${a.not_reached} not reached`, testId: 'act-voicemails' },
    { icon: Mail, label: 'Emails sent', value: a.emails_sent, sub: 'personalised by LEO', testId: 'act-emails' },
    { icon: MessageCircle, label: 'WhatsApp + SMS', value: a.whatsapp_sent + a.sms_sent, sub: `${a.whatsapp_sent} WA · ${a.sms_sent} SMS`, testId: 'act-messaging' },
    { icon: Linkedin, label: 'LinkedIn actions', value: a.linkedin_actions, sub: `${a.linkedin_accepted} accepted`, testId: 'act-linkedin' },
    { icon: CalendarCheck, label: 'Meetings booked', value: a.meetings_booked, sub: `${a.meetings_held} held · ${a.meetings_awaiting_outcome ?? 0} to confirm · ${a.meetings_no_show} no-show`, testId: 'act-meetings' },
    { icon: Reply, label: 'Replies handled', value: a.replies_handled, sub: 'auto-classified & routed', testId: 'act-replies' },
    { icon: Search, label: 'Leads sourced', value: a.leads_sourced, sub: `of ${a.leads_total} new leads`, testId: 'act-sourced' },
    { icon: Target, label: 'Leads scored vs ICP', value: a.leads_scored, sub: `${a.won} won`, testId: 'act-scored' },
  ];
  return (
    <Card data-testid="leo-impact-activity">
      <CardHeader className="pb-3">
        <CardTitle className="text-base md:text-lg">What LEO actually did</CardTitle>
        <p className="text-xs text-muted-foreground">Counts come straight from call, message, meeting and lead records — each row is auditable in the CRM.</p>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-9">
          {tiles.map(t => (
            <div key={t.label} className="rounded-xl border border-border p-3 hover:border-emerald-500/50 transition-colors" data-testid={t.testId}>
              <t.icon className="h-4 w-4 text-muted-foreground" />
              <p className="text-xl font-semibold mt-2 tabular-nums">{t.value ?? 0}</p>
              <p className="text-xs font-medium">{t.label}</p>
              <p className="text-[11px] text-muted-foreground truncate" title={t.sub}>{t.sub}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm" data-testid="leo-impact-human-time-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground border-b">
                <th className="text-left py-2 font-medium">Human-equivalent effort</th>
                <th className="text-right py-2 font-medium">Volume</th>
                <th className="text-right py-2 font-medium">Min each</th>
                <th className="text-right py-2 font-medium">Human minutes</th>
              </tr>
            </thead>
            <tbody>
              {report.activity_rows.map((r, i) => (
                <tr key={r.label} className="border-b last:border-0" data-testid={`leo-impact-effort-${i}`}>
                  <td className="py-2 text-muted-foreground">{r.label}</td>
                  <td className="py-2 text-right tabular-nums">{r.count}{r.unit ? ` ${r.unit}` : ''}</td>
                  <td className="py-2 text-right tabular-nums">{r.unit ? '—' : r.minutes_each}</td>
                  <td className="py-2 text-right tabular-nums font-medium">{r.human_minutes.toLocaleString()}</td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="py-2">Total human hours to replicate</td>
                <td /><td />
                <td className="py-2 text-right tabular-nums" data-testid="leo-impact-total-hours">{report.human.hours_needed} h</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
